/**
 * ExecutiveProfessionalTemplate.js
 * ─────────────────────────────────────────────────────────────────────────────
 * ATS Rating: 97 / 100 — ATS Verified
 * Style: Authoritative executive — heavy typography, professional gravitas
 * Best For: C-suite, VP, Director, Senior Leadership roles
 */

import React from 'react';
import EditableField from '../components/EditableField';
import {
  normalizeTemplateData, BulletList, SkillsBlock,
  ContactRow, EmptyState, DS,
} from './templateDesignSystem';

function ExecutiveProfessionalTemplate({
  resumeData,
  accentColor = '#0f172a',
  fontScale = 1,
  editMode = false,
  onFieldUpdate = () => { },
  onDeleteBullet = null,
  onDeleteSkill = null,
}) {
  if (!resumeData) return <EmptyState />;
  const d = normalizeTemplateData(resumeData);
  const { name, role, summaryText, skills, experience, internships, projects, educationList, certList, achieveList, languageList } = d; // eslint-disable-line no-unused-vars
  const scale = fontScale || 1;
  const fs = (base) => `${parseFloat(base) * scale}px`;

  const ExecSection = ({ title }) => (
    <div style={{ marginTop: 14, marginBottom: 7 }}>
      <h2 style={{
        fontSize: fs(DS.fs.section), fontWeight: 800, color: '#fff',
        background: accentColor, padding: '4px 10px',
        textTransform: 'uppercase', letterSpacing: '1.4px',
        margin: 0, display: 'inline-block', fontFamily: 'inherit',
      }}>{title}</h2>
    </div>
  );

  return (
    <div id="resume" style={{ background: '#fff', color: '#111', fontFamily: 'Times New Roman, Georgia, serif', fontSize: fs(DS.fs.body), lineHeight: 1.55, padding: `${DS.sp.pageY} ${DS.sp.pageX}`, boxSizing: 'border-box' }}>

      {/* ─── Executive Header ─── */}
      <header style={{ marginBottom: 14, paddingBottom: 12 }}>
        <div style={{ borderLeft: `6px solid ${accentColor}`, paddingLeft: 14 }}>
          <EditableField editMode={editMode} value={name || ''} onSave={v => onFieldUpdate('name', v)} tag="h1"
            style={{ fontSize: fs('25px'), fontWeight: 900, color: '#0f172a', margin: 0, letterSpacing: '-0.5px', fontFamily: 'inherit' }} placeholder="Your Name" />
          {(role || editMode) && (
            <EditableField editMode={editMode} value={role || ''} onSave={v => onFieldUpdate('role', v)} tag="p"
              style={{ fontSize: fs('12px'), fontWeight: 600, color: accentColor, margin: '3px 0 6px', letterSpacing: '0.3px', textTransform: 'uppercase', fontFamily: 'inherit' }} placeholder="Chief Executive / VP / Director" />
          )}
          <ContactRow data={d} accentColor={accentColor} />
        </div>
        <div style={{ height: 2, background: accentColor, marginTop: 10 }} />
      </header>

      {/* ─── Executive Summary ─── */}
      {(summaryText || editMode) && (
        <section data-ats="summary" style={{ marginBottom: DS.sp.sectionMb }}>
          <ExecSection title="Executive Summary" />
          <EditableField editMode={editMode} value={summaryText} onSave={v => onFieldUpdate('summary', v)}
            tag="p" multiline style={{ fontSize: fs('10.5px'), color: '#374151', lineHeight: 1.7, margin: 0, fontStyle: 'italic', textAlign: 'justify', fontFamily: 'inherit' }} placeholder="Executive summary..." />
        </section>
      )}

      {/* ─── Core Competencies / Skills ─── */}
      {skills.length > 0 && (
        <section data-ats="skills" style={{ marginBottom: DS.sp.sectionMb }}>
          <ExecSection title="Core Competencies" />
          <SkillsBlock skills={skills} accentColor={accentColor} layout="rows" />
        </section>
      )}

      {/* ─── Professional Experience ─── */}
      {experience.length > 0 && (
        <section data-ats="experience" style={{ marginBottom: DS.sp.sectionMb }}>
          <ExecSection title="Professional Experience" />
          {experience.map((exp, idx) => (
            <div key={idx} style={{ marginBottom: 12 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', borderBottom: '1px solid #E5E7EB', paddingBottom: 3, marginBottom: 5 }}>
                <div>
                  <EditableField editMode={editMode} value={exp.role || ''} onSave={v => onFieldUpdate(`experience.${idx}.role`, v)}
                    tag="h3" style={{ fontSize: fs('11px'), fontWeight: 800, color: '#0f172a', margin: 0, fontFamily: 'inherit' }} />
                  {exp.company && (
                    <span style={{ fontSize: fs(DS.fs.body), color: '#6B7280', fontWeight: 600 }}>{exp.company}</span>
                  )}
                </div>
                <EditableField editMode={editMode} value={exp.duration || ''} onSave={v => onFieldUpdate(`experience.${idx}.duration`, v)}
                  tag="span" style={{ fontSize: fs(DS.fs.small), color: '#6B7280', whiteSpace: 'nowrap', marginLeft: 12, fontStyle: 'italic' }} />
              </div>
              <BulletList points={exp.points} accentColor={accentColor} />
            </div>
          ))}
        </section>
      )}

      {/* ─── Projects / Key Initiatives ─── */}
      {projects.length > 0 && (
        <section data-ats="projects" style={{ marginBottom: DS.sp.sectionMb }}>
          <ExecSection title="Key Initiatives" />
          {projects.map((proj, idx) => (
            <div key={idx} style={{ marginBottom: DS.sp.itemMb }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                <span style={{ fontSize: fs('10.5px'), fontWeight: 700, color: '#0f172a', fontFamily: 'inherit' }}>{proj.title || proj.name}</span>
                {proj.impact && <span style={{ fontSize: fs(DS.fs.small), color: '#059669', fontWeight: 600 }}>{proj.impact}</span>}
              </div>
              <BulletList points={proj.points} accentColor={accentColor} />
            </div>
          ))}
        </section>
      )}

      {/* ─── Education ─── */}
      {educationList.length > 0 && (
        <section data-ats="education" style={{ marginBottom: DS.sp.sectionMb }}>
          <ExecSection title="Education" />
          {educationList.map((edu, idx) => (
            <div key={idx} style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
              <div>
                <EditableField editMode={editMode} value={edu?.degree || ''} onSave={v => onFieldUpdate(`education.${idx}.degree`, v)}
                  tag="h3" style={{ fontSize: fs('10.5px'), fontWeight: 700, color: '#111', margin: 0, display: 'inline', fontFamily: 'inherit' }} />
                <span style={{ color: '#6B7280', fontSize: fs(DS.fs.body), marginLeft: 6 }}>{edu?.college}</span>
                {edu?.cgpa && <span style={{ fontSize: fs(DS.fs.small), color: '#9CA3AF', marginLeft: 6 }}>CGPA: {edu.cgpa}</span>}
              </div>
              <span style={{ fontSize: fs(DS.fs.small), color: '#9CA3AF', fontStyle: 'italic' }}>{edu?.year}</span>
            </div>
          ))}
        </section>
      )}

      {certList.length > 0 && (
        <section data-ats="certifications" style={{ marginBottom: DS.sp.sectionMb }}>
          <ExecSection title="Certifications" />
          <BulletList points={certList} accentColor={accentColor} />
        </section>
      )}
      {achieveList.length > 0 && (
        <section data-ats="achievements" style={{ marginBottom: DS.sp.sectionMb }}>
          <ExecSection title="Achievements" />
          <BulletList points={achieveList} accentColor={accentColor} />
        </section>
      )}
    </div>
  );
}

export default ExecutiveProfessionalTemplate;
